import FileUploadDialog from "@features/ax/shared/DocumentUploadModal";
import { PageHeader } from "@shared/components/PageHeader";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { toast } from "@shared/ui/use-toast";
import { downloadFileByUrl } from "@shared/utils/utils";
import { Download, Eye, FileText, Upload } from "lucide-react";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useUpdateDocuments } from "../../../hooks";

interface AssetDocumentsTabProps {
  data: any[];
}

const FileCard: React.FC<{ file: any }> = ({ file }) => {
  const handleDownload = () => {
    downloadFileByUrl(file.fileUrl);
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h4 className="font-medium text-gray-900 mb-2">{file.fileName}</h4>
            <div className="space-y-1 text-sm text-gray-600">
              <p>Date: {file.createdDate ?? "NA"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownload}
              className="h-8 w-8 p-0"
            >
              <Download className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownload}
              className="h-8 w-8 p-0"
            >
              <Eye className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const IncidentDocumentsTab: React.FC<AssetDocumentsTabProps> = ({
  data,
}) => {
  const { id } = useParams();
  const { mutateAsync: updateIncident, isPending: isUpdating } =
    useUpdateDocuments(id);
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  const handleFilesUpload = async (files: File[]) => {
    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("documents", file);
      });

      await updateIncident(formData);
    } catch (err: any) {
      toast({
        title: "Upload failed",
        description: err?.message || "Something went wrong while uploading",
        variant: "destructive",
      });
    }
  };

  const mediaExtensions = ["jpg", "jpeg", "png", "mp4"];
  const documentExtensions = ["pdf", "doc", "docx", "xls", "xlsx"];

  const mediaFiles = data?.filter((f) =>
    mediaExtensions.includes(f.fileName?.split(".").pop()?.toLowerCase())
  );

  const documentFiles = data?.filter((f) =>
    documentExtensions.includes(f.fileName?.split(".").pop()?.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center">
              <FileText className="h-4 w-4 text-blue-600" />
            </div>
            <PageHeader
              title="Files & Documents"
              subtitle="Asset-related documents, certificates, and attachments"
            />
          </div>
        </div>
        <Button
          className="flex items-center gap-2"
          onClick={() => setIsUploadOpen(true)}
        >
          <Upload className="h-4 w-4" />
          Upload Files
        </Button>
      </div>

      <FileUploadDialog
        isOpen={isUploadOpen}
        onOpenChange={setIsUploadOpen}
        onFilesUpload={handleFilesUpload}
        maxSizeInMB={100}
        title="Upload Asset Documents"
        description="Attach certificates, images, and other files related to this asset"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.mp4,.xls,.xlsx"
      />

      {mediaFiles?.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Media Files</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {mediaFiles.map((file, index) => (
              <FileCard key={index} file={file} />
            ))}
          </div>
        </div>
      )}

      {documentFiles?.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Documents</h3>
          <p className="text-sm text-gray-600 mb-4">
            Available files and documentation for this incident
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {documentFiles.map((file, index) => (
              <FileCard key={index} file={file} />
            ))}
          </div>
        </div>
      )}

      {data?.length === 0 && (
        <div className="space-y-4 ">
          <p className="text-sm text-gray-600 mb-4 text-center">
            No files and documentations are added for this incident
          </p>
        </div>
      )}
    </div>
  );
};
